import MyraAudioMonitor from "./myraAudioMonitor";

// VoiceService.js
const VoiceService = {
  // --- Private State ---
  _ws: null,
  _mediaRecorder: null,
  _isListening: false,
  _pendingAudioUrls: [], // Blob URLs waiting to be played until audio is unlocked
  _isPlayingLegacy: false, // only used for non-PCM (blob-URL) fallback
  _awaitingPcmFinish: false,
  _debugChunkCount: 0, // DEBUG: count mic chunks to verify flow
  _pcmBuffer: null,

  _listenersBound: false,
  _listenTimeoutId: null,
  _listenTimeoutMs: 920_000,
  _currentFlow: null,
  _currentFlowStopListen: null,
  _isInitialized: false,


  _lastReceivedId: null,
  _lastCallTime: 0,

  _callbacks: {
    onNluResult: () => { },
    onAgentSpeechStart: () => { },
    onAgentSpeechEnd: () => { },
    onAgentSpeechEndCustom: () => { },
    onListeningStart: () => { },
    onListeningStop: () => { },
    onAudioReady: () => { }, // fired when app/user has unlocked audioPlayer
    onConnected: () => { },
  },

  // --- Reconnect/heartbeat state (minimal additions) ---
  _url: null,
  _reconnectAttempts: 0,
  _shouldReconnect: true,
  _connecting: false,
  _connected: false,
  _heartbeatTimer: null,
  _pongTimer: null,
  _pingIntervalMs: 15_000,
  _pongTimeoutMs: 30_000,
  _maxBackoffMs: 30_000,

  initialize(callbacks = {}) {
    this._shouldReconnect = true;
    this._isInitialized = true;

    if (this._ws || this._connecting) {
      console.warn("VoiceService is already initialized.");
      return;
    }
    this._callbacks = { ...this._callbacks, ...callbacks };

    if (window.audioPlayer?.unlocked) {
      this._callbacks.onAudioReady();
    }

    var mainWSSServer = "wss://api.virdiohealth.com/ws/tts";
    if (window.location.host !== "www.startfreept.com" && window.location.host !== "startfreept.com") {
      mainWSSServer = "wss://uatapi.virdiohealth.com/ws/tts";
    }

    // this._url = "ws://localhost:8080/ws/tts";
    this._url = mainWSSServer;
    // network/tab awareness for opportunistic reconnects
    if (!this._listenersBound) {
      window.addEventListener("online", () => this._maybeReconnect("online"));
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") this._maybeReconnect("visible");
      });
      this._listenersBound = true; // add this private flag
    }

    this._connect();
  },

  notifyAudioReady() {
    this._callbacks.onAudioReady();
    this._flushPendingBlobUrls();
  },
  _ensureWebSocketReady() {
    if (!this._ws || this._ws.readyState !== WebSocket.OPEN) {
      console.warn("[WS] WebSocket not ready, state:", this._ws?.readyState);
      return false;
    }
    return true;
  },
  sendQNADataToBackend(qnaData) {
    if (!this._ensureWebSocketReady()) {
      console.error("WebSocket not connected. Cannot send QnA data.");
      return false;
    }

    this._ws.send(
      JSON.stringify({
        type: "qna_data",
        data: qnaData,
        uniqueId: this._generateUniqueId()
      })
    );
    return true;
  },
  proceedToRecoveryPlan(dynamicSpeech, serviceSwitch = "gemini", qnaData = null) {
    if (!this._ensureWebSocketReady()) {
      console.error("WebSocket is not connected. Cannot proceed to recovery plan.");
      return false;
    }
    if (qnaData) {
      this.sendQNADataToBackend(qnaData);
    }
    window.currentFlow = "MyraQnAService"

    this._ws.send(
      JSON.stringify({
        type: "start_flow",
        flow: "MyraQnAService",
        speech: dynamicSpeech,
        switch: serviceSwitch
      })
    );
    return true;
  },

  startFlow(flow, userName) {
    if (!this._ensureWebSocketReady()) {
      console.error("WebSocket is not connected. Cannot start flow.");
      // FIX: Attempt reconnection
      this._connect();
      return;
    }
    let runCommands = { type: "start_flow", startName: userName, flow };
    if (window.mayraForwardCode) {
      runCommands = { ...runCommands, ...window.mayraForwardCode };
      window.mayraForwardCode = undefined;
    }

    console.log("Starting flow", runCommands);
    this._ws.send(JSON.stringify(runCommands));
    this._currentFlow = flow;
  },

  close() {
    // NEW: prevent auto-reconnect on intentional close
    this._shouldReconnect = false;

    this._stopListening();
    if (this._ws) this._ws.close();
    this._cleanup();
  },

  // --- Private ---

  async _handleBackendMessage(event) {
    // Any message counts as activity; bump pong timer for heartbeat
    this._bumpPongTimer();

    // STEP 4: NOTIFY WHEN AUDIO RECEIVED (for WebSocket commands)
    if (this._audioMonitor) {
      this._audioMonitor.notifyAudioDataReceived();
    }

    // --- Replace inside _handleBackendMessage ---
    if (event.data instanceof ArrayBuffer) {
      const buf = new Uint8Array(event.data);

      // Accumulate into buffer
      if (!this._pcmBuffer) this._pcmBuffer = new Uint8Array(0);
      const merged = new Uint8Array(this._pcmBuffer.length + buf.length);
      merged.set(this._pcmBuffer, 0);
      merged.set(buf, this._pcmBuffer.length);
      this._pcmBuffer = merged;

      // Process only full 2-byte frames
      const usableLength = this._pcmBuffer.length - (this._pcmBuffer.length % 2);
      if (usableLength > 0 && window.audioPlayer?.pcm?.active) {
        const toSend = this._pcmBuffer.slice(0, usableLength);
        try {
          window.audioPlayer.ttsPcmPush(toSend.buffer);
        } catch (e) {
          console.error("PCM push failed:", e);
        }
        // keep leftover bytes (at most 1 byte)
        this._pcmBuffer = this._pcmBuffer.slice(usableLength);
      }
      return;
    }

    try {
      const command = JSON.parse(event.data);
      console.log("Got command", command);

      // STEP 4: NOTIFY WHEN AUDIO RECEIVED (for WebSocket commands)
      if (this._audioMonitor && command.type === "tts_listening") {
        this._audioMonitor.notifyAudioDataReceived();
      }

      this._handleBackendCommand(command);
    } catch (error) {
      console.error(error);
      console.error("Received non-JSON message:", event.data);
    }
  },
  resumeCompletely() {
    console.log("[VoiceService] Resuming completely...");
    try {
      this._startListening();
      this.isListening = true;
    } catch (err) {
      console.error("[VoiceService] Error while resuming:", err);
    }
  },
  stopCompletely() {
    console.log("[VoiceService] Stopping completely...");
    try {
      this.stopAgentSpeech();
      this._stopListening();
      this.isListening = false;
    } catch (err) {
      console.error("[VoiceService] Error while stopping:", err);
    }
  },
  getConnectionStatus() {

    if (!this._url && !this._ws && !this._connecting && !this._isInitialized) {
      return 'not_initialized';
    }
    // If we're in the process of connecting
    if (this._connecting) {
      return 'reconnecting';
    }

    // Check WebSocket state
    if (!this._ws) {
      return 'disconnected';
    }

    const wsState = this._ws.readyState;

    // Map WebSocket states to our connection status
    const stateMap = {
      [WebSocket.CONNECTING]: 'reconnecting',
      [WebSocket.OPEN]: 'connected',
      [WebSocket.CLOSING]: 'disconnected',
      [WebSocket.CLOSED]: 'disconnected'
    };

    return stateMap[wsState] || 'disconnected';
  },

  _handleBackendCommand(command) {
    if (command.uniqueId && this._lastReceivedId === command.uniqueId) {
      console.log("Disable same commands");
      return;
    }

    if (command.type === "pong") {
      console.log("PONG RECEIVED");
      return;
    }

    // Step 5: Notify monitor of Deepgram status changes
    if (command.type === "deepgram_connected" && this._audioMonitor) {
      this._audioMonitor.notifyDeepgramStatus(true);
    } else if (command.type === "deepgram_disconnected" && this._audioMonitor) {
      this._audioMonitor.notifyDeepgramStatus(false);
    }

    this._lastReceivedId = command.uniqueId;

    if (command.type === "pause_for_confirmation") {
      // Pause video during confirmation
      if (window.videoController) {
        window.videoController.pause();
      }

      // Visual indicator (optional)
      if (this._callbacks.onConfirmationNeeded) {
        this._callbacks.onConfirmationNeeded();
      }
    }

    if (command.type === "nlu_result") {
      // Clear the transcription from the banner when command is executed
      if (document.getElementById("debugVoice")) {
        document.getElementById("debugVoice").innerHTML = "";
      }
      if (command.data.action === "AUTO_PAUSE" && command.data.value === true) {
        console.log("[INACTIVITY] Auto-pause detected - showing modal");
        if (window.mayraCode) {
          const isEnabled = window.mayraCode.isEnabled ? true : false;
          const isVisible = window.mayraCode.isVisible ? true : false;
          if (isEnabled && isVisible) {
            window.mayraCode.doSay("I’ve paused. Take your time. Press the re-zume button when you’re ready.", () => {
              // Dispatch event
              document.dispatchEvent(
                new CustomEvent("showInactivityModal", {
                  detail: { reason: "inactivity_timeout" },
                })
              );
              setTimeout(() => {
                this.stopCompletely();
              }, 500);
            });
          }
        }
      }

      if (command.data.action === "TURN_OFF" && command.data.value === true) {
        console.log("[TURN_OFF] Global turn-off command received - stopping all voice activity");
        if (window.mayraCode) {
          window.mayraCode.doSay("I heard you say turn off. I'm turning off now.", () => {
            // this.stopAllVoiceActivity();

            // // Optionally notify the UI that Myra has been turned off
            // if (this._callbacks.onNluResult) {
            //   this._callbacks.onNluResult(command.data);
            // }

            // // Stop listening but keep WebSocket connection alive
            // this._stopListening();

            // // Clear current flow
            // this._currentFlow = null;

            // return;
            this.stopCompletely();
          });
        }
        setTimeout(() => {
          this.stopCompletely();
        }, 5000);
      }
      if (command.speech) {
        // Store the speech for the frontend to use for symptom1 flow
        command.data.customSpeech = command.speech;
      }
      if (command.intent) {
        // Store the intent for the frontend to use for recovery help flow
        command.data.intent = command.intent;
      }
      if (this._currentFlow !== "personalized") {
        // this._stopListening();
      }
      // First try global handler if available
      if (window.currentCommandsAudio && window.currentCommandsAudio[command.data.action]) {
        console.log("Using global command handler for:", command.data.action);
        const now = Date.now();
        if (now - this._lastCallTime > 500) {
          window.currentCommandsAudio[command.data.action](command.data.value, command.data.customSpeech, command.data.intent);
          this._lastCallTime = now;
        } else {
          console.log("Too fast call");
        }
      } else {
        // this._stopListening();
      }

      // Then call the internal callback
      if (this._callbacks.onNluResult) {
        this._callbacks.onNluResult(command.data);
      }
    }
    if (command.type === "personalized_keep_alive") {
      console.log("[PERSONALIZED] Keep alive received - maintaining microphone");
      // Ensure microphone stays on
      if (!this._isListening) {
        this._startListening();
      }
    }

    if (command.type === "personalized_complete") {
      console.log("[PERSONALIZED] Flow complete - stopping microphone");
      this.completePersonalization();
    }

    if (command.type === "agent_speech") {
      if (this._callbacks.onAgentSpeechStart) {
        this._callbacks.onAgentSpeechStart();
      }
      console.log("Agent will say:", command.message);
      document.getElementById("debugVoice").innerHTML = "";
    }

    if (command.type === "tts_start") {
      try {
        const sr = command.sampleRate || 24000;
        if (window.audioPlayer?.ttsPcmStart) window.audioPlayer.ttsPcmStart(sr);
        if (this._callbacks.onAgentSpeechStart) {
          this._callbacks.onAgentSpeechStart();
        }

        if (this._currentFlow === "help" && window.stopStartAudio && window.stopLightAudio) {
          window.stopStartAudio();
          window.stopLightAudio();
        }
      } catch (e) {
        console.warn("tts_start handling failed:", e);
      }
      this._startListening();
    }

    if (command.type === "tts_listening") {
      if (command.transcript && document.getElementById("debugVoice")) document.getElementById("debugVoice").innerHTML = command.transcript;
      // if (command.transcript && document.getElementById("customTextDebug")) document.getElementById("customTextDebug").innerHTML = command.transcript;
    }

    if (command.type === "tts_end") {
      /* try {
        if (window.audioPlayer?.ttsPcmEnd) window.audioPlayer.ttsPcmEnd();
      } catch (_) {}
      if (this._callbacks.onAgentSpeechEnd) {
        this._callbacks.onAgentSpeechEnd();
      }
      this._startListening();
    */
      this._awaitPcmFinish();
    }

    if (command.type === "tts_error") {
      console.warn("TTS backend error:", command.message);
      try {
        if (window.audioPlayer?.ttsPcmAbort) window.audioPlayer.ttsPcmAbort();
      } catch (_) { }
      if (this._callbacks.onAgentSpeechEnd) {
        this._callbacks.onAgentSpeechEnd();
      }
      if (this._callbacks.onAgentSpeechEndCustom) {
        this._callbacks.onAgentSpeechEndCustom();
      }
      this._startListening();
    }
  },
  completePersonalization() {
    if (this._currentFlow === "personalized") {
      this._stopListening();
      this._currentFlow = null;
    }
  },

  _awaitPcmFinish() {
    if (this._awaitingPcmFinish) return;
    this._awaitingPcmFinish = true;

    const poll = () => {
      if (!this._awaitingPcmFinish) {
        return;
      }

      const active = !!window.audioPlayer?.pcm?.active;
      const queued = window.audioPlayer?.pcm?.queuedSamples || 0;

      const isComplete = !active || queued === 0;

      if (isComplete) {
        this._awaitingPcmFinish = false;
        if (this._callbacks.onAgentSpeechEnd) {
          this._callbacks.onAgentSpeechEnd();
        }
        if (this._callbacks.onAgentSpeechEndCustom) {
          this._callbacks.onAgentSpeechEndCustom();
        }

        if (window.completedMyraSay) {
          window.completedMyraSay();
        }

        const restartListening = (attempt = 0) => {
          try {
            this._startListening();
            console.log("[AWAIT_PCM] Successfully restarted listening after PCM finish");
          } catch (error) {
            console.error(`[AWAIT_PCM] Attempt ${attempt + 1} failed:`, error);
            if (attempt < 2) {
              // Max 3 attempts
              setTimeout(() => restartListening(attempt + 1), 1000 * (attempt + 1));
            } else {
              console.error("[AWAIT_PCM] All restart attempts failed");
            }
          }
        };
        restartListening();
        return;
      }
      setTimeout(poll, 50);
    };
    poll();
    window.forceQuickComplete = poll;
  },

  async _playClipUrl(url) {
    const player = window.audioPlayer;
    if (!player?.unlocked) {
      console.log("Audio not unlocked yet; queuing blob URL");
      this._pendingAudioUrls.push(url);
      return;
    }

    try {
      await player.ensureCtxRunning?.();
      await player.playAudio(url);
      this._isPlayingLegacy = true;
    } catch (err) {
      console.error("Error playing blob URL via audioPlayer:", err);
    }
  },

  _flushPendingBlobUrls() {
    if (!window.audioPlayer?.unlocked) return;
    if (this._pendingAudioUrls.length === 0) return;
    console.log(`Flushing ${this._pendingAudioUrls.length} queued blob URLs`);
    const urls = [...this._pendingAudioUrls];
    this._pendingAudioUrls = [];
    urls.forEach((u) => this._playClipUrl(u));
  },

  _abToBlobUrl(arrayBuffer) {
    let mime = "";
    if (arrayBuffer && arrayBuffer.byteLength >= 4) {
      const u8 = new Uint8Array(arrayBuffer.slice(0, 4));
      if (u8[0] === 0x49 && u8[1] === 0x44 && u8[2] === 0x33) mime = "audio/mpeg"; // ID3
      else if (u8[0] === 0x4f && u8[1] === 0x67 && u8[2] === 0x67 && u8[3] === 0x53) mime = "audio/ogg"; // OggS
      else if (u8[0] === 0x1a && u8[1] === 0x45 && u8[2] === 0xdf && u8[3] === 0xa3) mime = "audio/webm"; // EBML
      else if (u8[0] === 0xff && (u8[1] & 0xe0) === 0xe0) mime = "audio/mpeg"; // MP3 frame sync
      else console.log("Unknown audio signature:", `${u8[0]}-${u8[1]}-${u8[2]}-${u8[3]}`);
    }
    const blob = mime ? new Blob([arrayBuffer], { type: mime }) : new Blob([arrayBuffer]);
    return URL.createObjectURL(blob);
  },
  _validateListeningState() {
    if (this._isListening && (!this._mediaRecorder || this._mediaRecorder.state !== "recording")) {
      console.warn("[STATE_VALIDATION] Listening state inconsistent, correcting...");
      this._isListening = false;
      this._startListening().catch((err) => console.error("[STATE_VALIDATION] Failed to correct state:", err));
    }
  },

  async _startListening() {
    console.log(`[START_LISTENING] State check - isListening: ${this._isListening}, MediaRecorder state: ${this._mediaRecorder?.state}`);
    // Checks three conditions instead of just one
    // stream.active catches when the MediaStream itself is dead
    // track.readyState === 'live' catches when individual audio tracks have ended
    // Forces cleanup when zombie state detected, allowing a fresh start
    const isRecorderActive = this._mediaRecorder && this._mediaRecorder.state === "recording";
    const isStreamActive = this._mediaRecorder?.stream?.active;
    const hasLiveTracks = this._mediaRecorder?.stream?.getAudioTracks().some(t => t.readyState === 'live');
    if (isRecorderActive && isStreamActive && hasLiveTracks) {
      console.log("[START_LISTENING] MediaRecorder is healthy and recording, reusing instance.");
      if (!this._isListening) {
        this._isListening = true;
      }
      return;
    } else if (isRecorderActive) {
      console.warn("[START_LISTENING] Zombie Recorder detected! State is recording but stream/tracks are dead. Forcing restart.");
      // Force a cleanup so we can start fresh
      this._resetMediaRecorderState();
    }

    // If MediaRecorder exists but isn't recording, but our flag says we're listening
    if (this._isListening && this._mediaRecorder && this._mediaRecorder.state !== "recording") {
      console.warn("[START_LISTENING] State inconsistency - isListening true but MediaRecorder not recording");
      this._isListening = false;
      // Don't return here - proceed to start fresh session
    }

    if (this._isListening) {
      console.log("[START_LISTENING] Already marked as listening but MediaRecorder not active - resetting");
      this._isListening = false;
    }

    try {
      this._debugChunkCount = 0;

      // ONLY reset if MediaRecorder exists and is in wrong state
      if (this._mediaRecorder && this._mediaRecorder.state !== "inactive") {
        console.log("[START_LISTENING] Cleaning up previous MediaRecorder instance");
        this._resetMediaRecorderState();
      }

      // Clean up any old stream tracks
      try {
        const oldStream = this._mediaRecorder?.stream;
        if (oldStream?.getTracks) {
          oldStream.getTracks().forEach((t) => {
            try {
              if (t.readyState === "live") {
                t.stop();
              }
            } catch (_) { }
          });
        }
      } catch (_) { }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: false,
          autoGainControl: true,
          sampleRate: 48000
        },
      });

      // STEP 3: UPDATE STREAM
      if (this._audioMonitor) {
        this._audioMonitor.updateStream(stream);
      }

      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const isEdge = /Edg/.test(navigator.userAgent);

      let chosenType = "";
      if (isSafari) {
        chosenType = ""; // Let Safari choose default
      } else if (isEdge) {
        if (MediaRecorder.isTypeSupported("audio/webm")) {
          chosenType = "audio/webm";
        }
      } else {
        const preferTypes = ["audio/ogg;codecs=opus", "audio/webm;codecs=opus"];
        for (const t of preferTypes) {
          if (MediaRecorder.isTypeSupported(t)) {
            chosenType = t;
            break;
          }
        }
      }

      console.log("[MIC] chosen mimeType:", chosenType || "(browser default)");

      // ADD MIMETYPE VALIDATION
      let mimeType = chosenType || undefined;
      if (mimeType && !MediaRecorder.isTypeSupported(mimeType)) {
        console.warn(`[MIC] MimeType ${mimeType} not supported, using default`);
        mimeType = undefined;
      }

      this._mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType,
        audioBitsPerSecond: 64000,
      });

      // Optional: ensure WS is open before first send
      const waitForWS = () =>
        new Promise((res, rej) => {
          if (this._ws?.readyState === WebSocket.OPEN) return res();
          const iv = setInterval(() => {
            if (this._ws?.readyState === WebSocket.OPEN) {
              clearInterval(iv);
              res();
            } else if (this._ws?.readyState === WebSocket.CLOSED) {
              clearInterval(iv);
              rej(new Error("WS closed"));
            }
          }, 20);
        });
      await waitForWS();
      await new Promise((r) => setTimeout(r, 500)); // Reduced from 1000 to 500

      const sendBuffer = (buf) => {
        if (!this._ws || this._ws.readyState !== WebSocket.OPEN) return;
        if (this._ws.bufferedAmount > 1_000_000) {
          setTimeout(() => sendBuffer(buf), 20);
        } else {
          this._ws.send(buf);
        }
      };

      let debugChunkCount = 0;

      this._mediaRecorder.onstart = () => {
        console.log("[MIC] MediaRecorder started successfully:", this._mediaRecorder.mimeType);
      };

      this._mediaRecorder.ondataavailable = (event) => {
        debugChunkCount++;
        // LOGGING ADDITION for Debugging
        if (event.data.size === 0) {
          console.warn("[MIC] WARNING: Browser sent empty audio chunk (0 bytes).");
        }

        if (debugChunkCount % 10 === 1) {
          console.log(`[MIC] chunk #${debugChunkCount}, size=${event.data?.size}`);
        }
        if (event.data.size > 0 && this._ws?.readyState === WebSocket.OPEN) {
          event.data.arrayBuffer().then((buffer) => {
            if (debugChunkCount % 10 === 1) {
              console.log(`[WS] sending ${buffer.byteLength} bytes to server`);
            }
            sendBuffer(buffer);
          });
        } else if (this._ws?.readyState !== WebSocket.OPEN) {
          // LOGGING ADDITION
          console.warn(`[MIC] Drop-off detected: Captured ${event.data.size} bytes but WebSocket is ${this._ws?.readyState}`);
        }
      };

      this._mediaRecorder.onstop = () => {
        console.log("[MIC] MediaRecorder stopped");
        if (this._ws?.readyState === WebSocket.OPEN) {
          this._ws.send(JSON.stringify({ type: "stt_end", stopFlow: this._currentFlowStopListen, manualStop: 1 }));
        }
        // Don't stop stream tracks here - let them be managed by the cleanup
      };

      this._mediaRecorder.onerror = (e) => {
        console.error("[MEDIARECORDER] Error:", e?.error || e);
        this._isListening = false;
        if (this._callbacks.onListeningStop) {
          this._callbacks.onListeningStop();
        }
      };

      // ADD START WITH ERROR HANDLING
      try {
        this._mediaRecorder.start(250);
        console.log("[MIC] MediaRecorder start command sent");
      } catch (startError) {
        console.error("[MIC] Failed to start MediaRecorder:", startError);
        // Clean up on failure
        this._resetMediaRecorderState();
        throw startError;
      }

      this._isListening = true;
      if (this._callbacks.onListeningStart) {
        this._callbacks.onListeningStart();
      }

      if (this._listenTimeoutId) clearTimeout(this._listenTimeoutId);
      if (this._currentFlow !== "personalized" && this._currentFlow !== "first_test") {
        this._listenTimeoutId = setTimeout(() => {
          console.log("[MIC] 30s timeout reached, stopping listening");
          this._stopListening();
        }, this._listenTimeoutMs);
      } else {
        console.log("[MIC] Personalized flow - no timeout set");
      }
    } catch (error) {
      console.error("Error accessing microphone:", error);

      // SPECIFIC ERROR HANDLING
      if (error.name === "InvalidStateError" || error.name === "NotSupportedError") {
        console.error(`[START_LISTENING] ${error.name}:`, error.message);
        this._resetMediaRecorderState();
      }

      if (this._callbacks.onListeningStop) {
        this._callbacks.onListeningStop();
      }

      const shouldRetry = error.name !== "NotAllowedError"; // Don't retry if permission denied

      if (shouldRetry) {
        console.log("[START_LISTENING] Retrying in 2 seconds...");
        setTimeout(() => {
          if (!this._isListening) {
            console.log("[START_LISTENING] Attempting automatic retry");
            this._startListening().catch((retryError) => {
              console.error("[START_LISTENING] Retry failed:", retryError);
            });
          }
        }, 2000);
      }
    }
  },
  // Add this helper method
  _getSupportedMimeType() {
    const mimeTypes = ["audio/webm;codecs=opus", "audio/ogg;codecs=opus", "audio/webm", "audio/mp4", ""];

    for (const mimeType of mimeTypes) {
      if (!mimeType || MediaRecorder.isTypeSupported(mimeType)) {
        console.log(`[MIMETYPE] Using: ${mimeType || "browser default"}`);
        return mimeType || undefined;
      }
    }

    console.warn("[MIMETYPE] No specific mimeType supported, using default");
    return undefined;
  },

  _stopListening() {
    console.log(`[STOP_LISTENING] State - isListening: ${this._isListening}, MediaRecorder state: ${this._mediaRecorder?.state}`);

    if (!this._isListening && (!this._mediaRecorder || this._mediaRecorder.state !== "recording")) {
      console.log("[STOP_LISTENING] Already stopped, skipping");
      return;
    }
    // if (!this._isListening) return;

    console.log("Stop listening", this._currentFlow);

    this._currentFlowStopListen = this._currentFlow;

    if (this._listenTimeoutId) {
      clearTimeout(this._listenTimeoutId);
      this._listenTimeoutId = null;
    }

    if (this._mediaRecorder && this._mediaRecorder.state === "recording") {
      console.log("[STOP_LISTENING] Stopping MediaRecorder, state:", this._mediaRecorder.state);
      try {
        this._mediaRecorder.stop();
      } catch (stopError) {
        console.error("[STOP_LISTENING] Error stopping MediaRecorder:", stopError);
      }
    }
    this._isListening = false;
    if (this._callbacks.onListeningStop) {
      this._callbacks.onListeningStop();
    }
  },
  _resetMediaRecorderState() {
    console.log("[RESET_STATE] Resetting MediaRecorder state");
    this._isListening = false;

    if (this._mediaRecorder) {
      try {
        if (this._mediaRecorder.state === "recording") {
          console.log("[RESET_STATE] Stopping active MediaRecorder");
          this._mediaRecorder.stop();
        }
      } catch (e) {
        console.warn("[RESET_STATE] Error stopping MediaRecorder:", e);
      }

      // Clean up stream tracks
      try {
        const oldStream = this._mediaRecorder.stream;
        if (oldStream?.getTracks) {
          oldStream.getTracks().forEach((t) => {
            try {
              if (t.readyState === "live") {
                t.stop();
              }
            } catch (_) { }
          });
        }
      } catch (e) {
        console.warn("[RESET_STATE] Error cleaning up stream:", e);
      }
    }

    this._mediaRecorder = null;
  },

  stopAgentSpeech() {
    // Stop any ongoing TTS playback
    try {
      if (window.audioPlayer?.ttsPcmAbort) window.audioPlayer.ttsPcmAbort();
    } catch (error) {
      console.warn("Error stopping PCM playback:", error);
    }

    // Clear any pending audio URLs
    this._pendingAudioUrls.forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch (_) { }
    });
    this._pendingAudioUrls = [];
  },

  forceStop() {
    this.stopAgentSpeech();
    this._stopListening();

    // Send message to backend to stop any ongoing processing
    if (this._ws && this._ws.readyState === WebSocket.OPEN) {
      this._ws.send(JSON.stringify({ type: "stt_end", stopFlow: this._currentFlowStopListen, forceStop: 1 }));
    }
  },
  stopAllVoiceActivity() {
    console.log("[VoiceService] Stopping all voice activity");
    // Stop any TTS playback
    if (window.audioPlayer?.ttsPcmAbort) {
      window.audioPlayer.ttsPcmAbort();
    }
    // Clear any pending audio URLs
    this._pendingAudioUrls.forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch (_) { }
    });
    this._pendingAudioUrls = [];
  },
  _generateUniqueId() {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  },
  destroyMyraCompletely() {
    console.log("[DESTROY] Starting complete Myra shutdown");

    // PREVENT AUTO-RECONNECT
    this._shouldReconnect = false;

    if (window.monitorStopMyra) {
      window.monitorStopMyra();
    }

    // Stop all voice activity
    this.stopAllVoiceActivity();
    this._stopListening();
    this._resetMediaRecorderState();

    if (window.monitorStopMyraStream) { window.monitorStopMyraStream(); }

    // Send destroy command through CURRENT WebSocket
    if (this._ws && this._ws.readyState === WebSocket.OPEN) {
      this._ws.send(JSON.stringify({
        type: "destroy_myra",
        uniqueId: this._generateUniqueId()
      }));
    }

    // Force close WebSocket
    if (this._ws) {
      this._ws.close(1000, "Destroy requested");
    }

    // Complete cleanup
    this._cleanup();

    // Reset ALL state for fresh start
    this._resetAllState();
    this._shouldReconnect = false;

    console.log("[DESTROY] Myra completely destroyed - ready for fresh start");
  },

  _resetAllState() {
    this._pendingAudioUrls = [];
    this._isPlayingLegacy = false;
    this._awaitingPcmFinish = false;
    this._debugChunkCount = 0;
    this._pcmBuffer = null;
    this._listenTimeoutId = null;
    this._currentFlowStopListen = null;
    this._lastReceivedId = null;
    this._lastCallTime = 0;

    // Clear all timers
    if (this._heartbeatTimer) clearInterval(this._heartbeatTimer);
    if (this._pongTimer) clearTimeout(this._pongTimer);

    this._heartbeatTimer = null;
    this._pongTimer = null;

    this._isListening = false;
    this._currentFlow = null;
    this._connected = false;
    this._connecting = false;
    this._reconnectAttempts = 0;
    this._shouldReconnect = true;
  },

  setHelpState(helpDataArray) {
    if (!this._ws || this._ws.readyState !== WebSocket.OPEN) {
      console.error("WebSocket is not connected. Cannot set help state.");
      return false;
    }

    try {
      this._ws.send(
        JSON.stringify({
          type: "set_help_state",
          helpData: helpDataArray, // Array: [state, context]
        })
      );
      return true;
    } catch (error) {
      console.error("Error setting help state:", error);
      return false;
    }
  },

  _cleanup() {
    this._stopListening();
    this._resetMediaRecorderState();
    this._ws = null;
    this._pendingAudioUrls.forEach((u) => {
      try {
        URL.revokeObjectURL(u);
      } catch (_) { }
    });
    this._pendingAudioUrls = [];
    this._isPlayingLegacy = false;
    this._awaitingPcmFinish = false;

    // NEW: clear heartbeat timers
    this._clearHeartbeat();
    this._clearPongTimer();
  },

  // --- NEW: Minimal reconnect + heartbeat helpers ---

  _connect() {
    if (!this._url || this._connecting) return;
    if (!this._shouldReconnect && this._ws) return;

    this._connecting = true;

    // Store whether we were listening before disconnect - ENHANCE THIS
    const wasListening = this._isListening;
    const previousFlow = this._currentFlow;

    // Store in persistent storage for reconnection
    sessionStorage.setItem("voiceService_wasListening", wasListening.toString());
    sessionStorage.setItem("voiceService_currentFlow", previousFlow || "");

    // this._ws = new WebSocket("ws://localhost:8080/ws/tts");
    this._ws = new WebSocket(this._url);
    this._ws.binaryType = "arraybuffer";

    this._ws.onopen = () => {
      console.log("Connected to backend WebSocket");
      this._connecting = false;
      this._connected = true;
      this._reconnectAttempts = 0;

      // RESTORE LISTENING STATE - ENHANCED VERSION
      const wasListening = sessionStorage.getItem("voiceService_wasListening") === "true" || true;
      const previousFlow = sessionStorage.getItem("voiceService_currentFlow");

      // STEP 1: CREATE MONITOR - WebSocket connects
      if (!this._audioMonitor) {
        this._audioMonitor = new MyraAudioMonitor({
          websocket: this._ws, // Pass the connected WebSocket
        });

        // STEP 2: START IT
        this._audioMonitor.start().then((success) => {
          if (success) {
            console.log("[AUDIO MONITOR] Started successfully");
          }
        });

        window.monitorStopMyra = this._audioMonitor.stop;
        window.monitorStopMyraStream = this._audioMonitor.stopStream;
      }
      // FIX: Restart listening if we were listening before disconnect
      if (wasListening && previousFlow) {
        console.log(`[RECONNECT] Restarting listening for flow: ${previousFlow}`);
        // Clear any existing timeout
        if (this._listenTimeoutId) {
          clearTimeout(this._listenTimeoutId);
          this._listenTimeoutId = null;
        }

        setTimeout(() => {
          this._startListening().catch((err) => {
            console.error("[RECONNECT] Failed to restart listening:", err);
            // Retry with backoff
            setTimeout(() => {
              this._startListening().catch((e) => console.error("[RECONNECT] Retry failed:", e));
            }, 2000);
          });
        }, 1000);
      }

      if (this._callbacks.onConnected) {
        this._callbacks.onConnected();
      }
      // start heartbeat after successful open
      this._startHeartbeat();
      // optional hello/keepalive
      try {
        this._ws.send(JSON.stringify({ type: "hello", ts: Date.now() }));
      } catch { }
    };

    this._ws.onclose = () => {
      console.log("Disconnected from backend WebSocket");
      this._connected = false;
      this._cleanup();
      this._scheduleReconnect("close");
    };

    this._ws.onerror = (error) => {
      console.error("WebSocket error:", error);
      // errors often precede close; let onclose drive reconnect/backoff
    };

    this._ws.onmessage = (event) => {
      this._handleBackendMessage(event);
    };
  },

  _maybeReconnect(trigger) {
    if (!this._shouldReconnect) return;
    if (this._ws && (this._ws.readyState === WebSocket.OPEN || this._ws.readyState === WebSocket.CONNECTING)) return;
    if (this._connecting) return;
    if (!navigator.onLine) return;
    this._connect();
  },

  _backoffDelay() {
    const base = 1000; // 1s
    const exp = Math.min(10, this._reconnectAttempts);
    const raw = Math.min(this._maxBackoffMs, base * Math.pow(2, exp));
    const jitter = Math.floor(Math.random() * 1000);
    return raw + jitter;
  },

  _scheduleReconnect(reason) {
    if (!this._shouldReconnect) return;
    if (!navigator.onLine) {
      // will try again when 'online' fires
      return;
    }
    const delay = this._backoffDelay();
    this._reconnectAttempts += 1;
    setTimeout(() => this._connect(), delay);
  },

  _startHeartbeat() {
    this._clearHeartbeat();
    this._bumpPongTimer(); // expect activity soon
    this._heartbeatTimer = setInterval(() => {
      try {
        if (this._ws && this._ws.readyState === WebSocket.OPEN) {
          console.log("PING SENT");
          this._ws.send(JSON.stringify({ type: "ping", ts: Date.now() }));
          this._bumpPongTimer();
        }
      } catch { }
    }, this._pingIntervalMs);
  },

  _clearHeartbeat() {
    if (this._heartbeatTimer) {
      clearInterval(this._heartbeatTimer);
      this._heartbeatTimer = null;
    }
  },

  _bumpPongTimer() {
    this._clearPongTimer();
    this._pongTimer = setTimeout(() => {
      // no messages in too long: force-close to trigger reconnect path
      if (this._ws && this._ws.readyState === WebSocket.OPEN) {
        try {
          console.warn("Closing WS due to timeout pong");
          this._ws.close();
        } catch { }
      }
    }, this._pongTimeoutMs);
  },

  _clearPongTimer() {
    if (this._pongTimer) {
      clearTimeout(this._pongTimer);
      this._pongTimer = null;
    }
  },
};

window.MyraAudioMonitor = {
  getStatus: () => (VoiceService._audioMonitor ? VoiceService._audioMonitor.getHealth() : null),
  forceReconnect: () => (VoiceService._audioMonitor ? VoiceService._audioMonitor.forceReconnect() : Promise.resolve(false)),
  isMonitoring: () => (VoiceService._audioMonitor ? VoiceService._audioMonitor.isMonitoring : false),
};

window.VoiceService = VoiceService;

export default VoiceService;
